import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid, Typography } from '@mui/material';
import { Accordion } from 'components/molecules/Accordion';
import { EDictionaryTypes } from 'models/dictionary';
import { useNavigate, useParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { useLang } from 'models/langContext';
import { Alert } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { clearAlertTimeout, EAlertTime } from 'utils/date';
import { ActionTypes } from 'state/actions/alert';
import { useConfigurationLicence } from '../hooks';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { StyledLicenceWrapper } from './styles';
import { findInDictionary } from 'helpers/dictionary';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { priceFormat } from 'helpers/price';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { Chips } from 'components/atoms/Chips';
import { EApplicationProcessStatus, statusApplicationProcessChipVariant } from '../helper';
import { LETypes } from 'constants/LegalEntityTypes';
import { getHtmlText } from 'components/organisms/HtmlSlate';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { FormModal } from 'components/molecules/Modal';
import { LicenceTemplateApplicationProcessTasks } from '../LicenceTemplateApplicationProcessTasks';
import { LicenceTemplateApplicationProcessDocument } from '../LicenceTemplateApplicationProcessDocument';
import { EPermission } from 'models/permissions';
import { LicenceTemplateApplicationProcessIntelligenceReports } from '../LicenceTemplateApplicationProcessIntelligenceReports';

const LicenceTemplateApplicationProcessPage = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { currency } = useGlobalProperty();
  const { processId = '', id = '' } = useParams<{ processId: string; id: string }>();
  const { nextPageAlert, samePageAlert, clearAlert } = useAlert();
  const { selectedLanguage } = useLang();
  const {
    getLicenceApplicationProccessDetail,
    handleActivateApplicationProccess,
    handleDeactivateApplicationProccess,
    licenceApplicationProccess,
    loadDictionaries,
    applicationProcessTypes,
    applicationProcessStatus,
  } = useConfigurationLicence();
  const [isLoading, setIsLoading] = useState(true);
  const [isDeactivateButton, setIsDeactivateButton] = useState<boolean>();
  const [isActivateDeactivateModal, setIsActivateDeactivateModal] = useState(false);
  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useEffect(
    () => {
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_NEXT_PAGE_ALERT, EAlertTime.LONG);
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_SAME_PAGE_ALERT, EAlertTime.LONG);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    (async function init() {
      await loadDictionaries([EDictionaryTypes.applicationProcessTypes]);
      await loadDictionaries([EDictionaryTypes.applicationProcessStatus]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async function init() {
      setIsLoading(true);
      getLicenceApplicationProccessDetail(processId);
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processId]);

  useEffect(() => {
    if (licenceApplicationProccess) {
      setIsDeactivateButton(
        licenceApplicationProccess.statusUniqueId !== EApplicationProcessStatus.active,
      );
    }
  }, [licenceApplicationProccess]);

  const handleApprove = async () => {
    if (isDeactivateButton) {
      await handleActivateApplicationProccess(processId);
    } else {
      await handleDeactivateApplicationProccess(processId);
    }
    getLicenceApplicationProccessDetail(processId);
    setIsActivateDeactivateModal(false);
  };

  return (
    <>
      {isActivateDeactivateModal && (
        <FormModal
          open={isActivateDeactivateModal}
          onClickClose={() => {
            setIsActivateDeactivateModal(false);
          }}
          onClickApprove={handleApprove}
          title={
            isDeactivateButton
              ? t('applicationProcess.activate.label')
              : t('applicationProcess.deactivate.label')
          }
          text={
            isDeactivateButton
              ? t('applicationProcess.activate.text')
              : t('applicationProcess.deactivate.text')
          }
          titleLeft
          approveButtonLabel="general.yes.label"
          cancelButtonLabel="general.no.label"
        />
      )}
      <Breadcrumbs
        items={[
          {
            label: t('module.administration.name'),
            route: ERouteLinks.Administration,
          },
          {
            label: t('licenceConfiguration.header'),
            route: ERouteLinks.LicenceConfigurationPage,
          },
          {
            label: t('licenceConfiguration.detail.header'),
            route: ERouteLinks.LicenceTemplatePage.replace(':id', id),
          },
          t('licence.applicationProcess.details.header'),
        ]}
      />
      <Grid
        container
        size={{ xs: 12 }}
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Grid>
          <StyledPageTitle variant="h4">
            {t('licence.applicationProcess.details.header')}
          </StyledPageTitle>
        </Grid>
        <Grid sx={{ width: { xs: '100%', sm: 'auto' } }}>
          {isDeactivateButton !== undefined && (
            <Button
              label={t(
                `licence.applicationProcess.${!isDeactivateButton ? 'deactivate' : 'activate'}.link`,
              )}
              styleVariant={!isDeactivateButton ? 'error' : 'success'}
              id={`applicationProcess_${!isDeactivateButton ? 'deactivate' : 'activate'}-button`}
              variant={EButtonVariants.contained}
              onClick={() => {
                setIsActivateDeactivateModal(true);
              }}
              size="small"
              fullWidth
              sx={{ minWidth: { sm: '120px' } }}
            />
          )}
        </Grid>
      </Grid>

      {nextPageAlert && (
        <Grid sx={{ my: 1 }}>
          <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
        </Grid>
      )}
      {samePageAlert && (
        <Grid sx={{ my: 1 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Grid>
      )}

      {!isLoading && (
        <>
          <Accordion
            title={t('licence.generalSettings.label')}
            name="generalSettings"
            key="generalSettings"
            alwaysExpanded
            hideExpandIcon
            buttons={
              <Grid container sx={{ justifyContent: 'flex-end' }}>
                <Button
                  label={t('licence.applicationProcess.edit.link')}
                  id="applicationProcessSettingsEdit"
                  variant={EButtonVariants.outlined}
                  permission={EPermission.MANAGE_WEAPON_LICENCE_CONFIGURATOR}
                  onClick={() =>
                    navigate(
                      ERouteLinks.EditLicenceTemplateApplicationProcessSettingsPage.replace(
                        ':id',
                        id,
                      ).replace(':processId', processId),
                    )
                  }
                  size="small"
                  sx={{ minWidth: '94px' }}
                />
              </Grid>
            }
          >
            <StyledLicenceWrapper container>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('licence.applicationProcess.processType.label')}
                </Typography>
                <Typography variant="body2">
                  {licenceApplicationProccess?.processTypeUniqueId
                    ? findInDictionary(
                        licenceApplicationProccess?.processTypeUniqueId,
                        applicationProcessTypes,
                      )
                    : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('licence.applicationProcess.relatedLegalEntityType.label')}
                </Typography>
                <Typography variant="body2">
                  {licenceApplicationProccess?.legalEntityType === LETypes.individual
                    ? t('legalEntity.individual.label')
                    : t('legalEntity.organisation.label')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('licence.applicationProcess.status.label')}
                </Typography>
                <Typography variant="body2">
                  {licenceApplicationProccess?.statusUniqueId ? (
                    <Chips
                      text={findInDictionary(
                        licenceApplicationProccess?.statusUniqueId,
                        applicationProcessStatus,
                      )}
                      variant={statusApplicationProcessChipVariant(
                        licenceApplicationProccess.statusUniqueId,
                      )}
                    />
                  ) : (
                    ''
                  )}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('licence.applicationProcess.comment.label')}
                </Typography>
                <Typography variant="body2">
                  {emptyFieldHolder(getHtmlText(licenceApplicationProccess?.comment))}
                </Typography>
              </Grid>
            </StyledLicenceWrapper>
          </Accordion>
          <Accordion
            title={t('licence.price.label')}
            name="price"
            key="price"
            alwaysExpanded
            hideExpandIcon
            buttons={
              <Grid container sx={{ justifyContent: 'flex-end' }}>
                <Button
                  label={t('licence.applicationProcess.edit.link')}
                  id="applicationProcessPriceEdit"
                  variant={EButtonVariants.outlined}
                  permission={EPermission.MANAGE_WEAPON_LICENCE_CONFIGURATOR}
                  onClick={() =>
                    navigate(
                      ERouteLinks.EditLicenceTemplateApplicationProcessPricePage.replace(
                        ':id',
                        id,
                      ).replace(':processId', processId),
                    )
                  }
                  size="small"
                  sx={{ minWidth: '94px' }}
                />
              </Grid>
            }
          >
            <StyledLicenceWrapper container size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('licence.applicationProcess.priceName.label')}
                </Typography>
                <Typography variant="body2">
                  {emptyFieldHolder(licenceApplicationProccess?.priceName)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('licence.applicationProcess.price.label')}
                </Typography>
                <Typography variant="body2">
                  {`${priceFormat(licenceApplicationProccess?.priceFee)} ${currency}`}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('licence.applicationProcess.priceDescription.label')}
                </Typography>
                <Typography variant="body2">
                  {getHtmlText(licenceApplicationProccess?.priceDescription)}
                </Typography>
              </Grid>
            </StyledLicenceWrapper>
          </Accordion>
          <LicenceTemplateApplicationProcessTasks />
          <LicenceTemplateApplicationProcessIntelligenceReports />
          <LicenceTemplateApplicationProcessDocument />
        </>
      )}
    </>
  );
};

export { LicenceTemplateApplicationProcessPage };
